import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import i18n from 'i18n';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  IDragAndDropDataFormat,
  ITopicDnDResponse,
  ITopicDnDResponseOptionalGuid,
  topicKeysRequestFormat,
} from 'Data/interfaces/Activities/IDragAndDropDataFormat';
import { ActivitiesActions } from 'Store/Activities/Activities.actions';
import useQuery from 'Hooks/useQuery';
import useWindowSize from 'Hooks/useWindowSize';
import { ITableActionTopbarButton } from 'Components/UI/TableActionTopbar';
import AccessPermission from 'Utils/AcessPermission';
import toastHandler from '../../Utils/toastHandler';
import { ITopicSignalRHasUpdate } from 'Data/interfaces/Activities/ITopicSignalRHasUpdate';
import { TopicVisibilityEnum } from 'Data/interfaces/Activities/ITopicResponse';
import { useFeatureFlag } from 'Hooks/useFeatureFlag';
import { IObraResponse } from 'Data/interfaces/Obra/IObraListResponse';
import { TypeListButtons } from './components/ListButtons';
import AppStorage from '../../Utils/AppStorage';
import { getFiltersKey } from 'Utils/ActivitiesUtils';
import { FiltersRecordType, IUpdateFilter } from 'Data/interfaces/Activities/IFilters';
import { IUserInforResponse } from 'Data/interfaces/Auth/IUserInfoResponse';
import { Mixpanel } from 'Utils/MixPanel';
import { v4 as uuid } from 'uuid';
import MoreFiltersActions from './components/MoreFiltersActions';
import { IPrintDetailsSettings } from './Modals/PrintDetails';
import { ITopicRequest } from 'Data/interfaces/Activities/ITopicRequest';
import {
  FilterType,
  IClearFiltersRequestUpdatedDate,
  IFiltersData,
  IFiltersRequest,
} from 'Data/interfaces/Activities/IFiltersRequest';
import { FilterApi } from 'Data/FilterApi';
import { BUTTON_ACTIVITY_LIST_KEY } from '.';
import { getCurrentTenant, getTenants } from 'Store/Tenant/Tenant.selector';
import { useQueryUpdatedDate } from 'Hooks/activities/useQueryUpdatedDate';
import { useQueryReadMultipleNotifications } from 'Hooks/notifications/useQueryReadMultipleNotifications';
import {
  getIsLoadingCreate,
  getIsLoadingUpdate,
  getReadNotificationsData,
} from 'Store/Activities/Activities.selector';
import { useQueryInAppTopicNotifications } from 'Hooks/notifications/useQueryInAppTopicNotifications';
import { useTimeout } from 'Context/TimeoutContext';
import { QUERY_KEY_ACTIVITIES_UPDATED_DATE } from 'Data/keys/QueryKeys';
import { queryClient } from 'Utils/queryClient';
import dayjs from 'dayjs';
import {
  IFiltroOptions,
  enumToStringValueMap,
} from 'Data/interfaces/Activities/IFilters';
import {
  complexfilterActivities,
  filterActivities,
} from 'Utils/ActivitiesUtils';

export type TypeMoreActionsType = 'createAndOpen' |
  'createAndNew' |
  'createAndCopy' |
  'update' |
  'duplicate' |
  null;

interface IUseActivity {
  constructionSiteId: number;
  listActivities?: IDragAndDropDataFormat;
  totalActivitiesCount?: number;
  topicSignalRHasUpdate: ITopicSignalRHasUpdate | null;
  userInfo?: IUserInforResponse;
  externalGlobalLoading: boolean;
  obrasList?: IObraResponse[];
  shareLink?: string;
  filters: FiltersRecordType;
  fromUpDate?: string;
  toUpDate?: string;
}

const useActivity = ({
  constructionSiteId,
  listActivities,
  totalActivitiesCount,
  topicSignalRHasUpdate,
  userInfo,
  externalGlobalLoading,
  obrasList,
  shareLink,
  filters,
}: IUseActivity) => {
  const { t } = useTranslation('activities');
  const dispatch = useDispatch();
  const { set, clear } = useTimeout();

  const stagesFeatureActivityValidateFlag = useFeatureFlag(
    'stages-feature-activity-validate',
  );
  const stagesFeatureActivityValidate =
    stagesFeatureActivityValidateFlag.enabled &&
    stagesFeatureActivityValidateFlag.value === 'test';

  const tenants = useSelector(getTenants);
  const navigate = useNavigate();
  const query = useQuery();
  const { height, width } = useWindowSize();
  const { readMultipleNotificationsAsync } = useQueryReadMultipleNotifications();
  const {
    data: inAppTopicNotifications,
    refetch: refetchInAppTopic,
  } = useQueryInAppTopicNotifications(constructionSiteId);

  const currentTenant = useSelector(getCurrentTenant);
  const readNotificationsData = useSelector(getReadNotificationsData);
  const isLoadingCreate = useSelector(getIsLoadingCreate);
  const isLoadingUpdate = useSelector(getIsLoadingUpdate);

  const filtersIdQuery = query.get('filterId');
  const searchActivityQuery = query.get('searchActivity');
  const workActivityQuery = query.get('workActivity');
  const isNotifQuery = query.get('isNotif');
  const fromUpDateQuery = query.get('updatedFrom');
  const toUpDateQuery = query.get('updatedTo');

  const firstRender = useRef(0);
  const firstRenderSignal = useRef(true);
  const firstOpenFilters = useRef(true);
  const firstRenderFilters = useRef(true);
  const firstRenderNotif = useRef(0);
  const blockFilterDropdown = useRef(true);
  const [searchActivities, setSearchActivities] = useState<string>('');
  const [searchActivitiesDefault, setSearchActivitiesDefault] = useState<
    string | undefined
  >(undefined);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeListButton, setActiveListButton] = useState<TypeListButtons>(
    (AppStorage.GetItem(BUTTON_ACTIVITY_LIST_KEY, 'local') as TypeListButtons) || 'kanban',
  );
  const [showCreateActivity, setShowCreateActivity] = useState(false);
  const [currentShowActivity, setCurrentShowActivity] = useState<string | null>(null);
  const [lastModifiedLoading, setLastModifiedLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showFiltersShare, setShowFiltersShare] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [showPrintDetailsModal, setShowPrintDetailsModal] = useState(false);
  const [lastFilterUpdateId, setLastFilterUpdateId] = useState('');
  const [moreActionsType, setMoreActionsType] = useState<TypeMoreActionsType>(null);
  const hasFilters = JSON.stringify(filters) !== '{}';
  const listActivitiesArray = useMemo(() => {
    return Object.values(listActivities?.activities || {});
  }, [listActivities?.activities]);

  const filterUpdatedDate = useMemo(() => {
    const filterData: IClearFiltersRequestUpdatedDate = { from: null, to: null };
    if (!filters) { return filterData; }

    const filterArray = Object.values(filters);
    const updatedDateFilter = filterArray.findIndex((filter) => {
      return filter.topicId === 'UpdatedDate';
    });
    if (updatedDateFilter !== -1) {
      const filter = filterArray[updatedDateFilter];
      let value1 = null;
      let value2 = null;
      if (Array.isArray(filter.value) && filter.value.length > 1) {
        value1 = dayjs(filter.value[0]);
        value2 = dayjs(filter.value[1]);
      } else {
        value1 = dayjs(filter.value);
      }
      switch (filter.operation) {
        case 'equal':
          filterData.from = value1.format('YYYY-MM-DD');
          filterData.to = value1.format('YYYY-MM-DD');
          break;
        case 'between':
          if (value1 && value2) {
            filterData.from = value1.format('YYYY-MM-DD');
            filterData.to = value2.format('YYYY-MM-DD');
          }
          break;
        case 'before':
          filterData.to = value1.subtract(1, 'day').format('YYYY-MM-DD');
          break;
        case 'after':
          filterData.from = value1.add(1, 'day').format('YYYY-MM-DD');
          break;
        case 'onOrBefore':
          filterData.to = value1.format('YYYY-MM-DD');
          break;
        case 'onOrAfter':
          filterData.from = value1.format('YYYY-MM-DD');
          break;
      }
    }
    return filterData;
  }, [filters]);
  const {
    data: dataUpdatedDate,
    isFetching: isFetchingUpdatedDate,
    refetch: refetchUpdatedDate,
  } = useQueryUpdatedDate({
    csId: constructionSiteId,
    from: filterUpdatedDate.from,
    to: filterUpdatedDate.to,
  });

  const filteredActivities = useMemo(() => {
    let currentActivities = complexfilterActivities<ITopicDnDResponse, typeof enumToStringValueMap>({
      filters: filters as Record<string, IFiltroOptions<ITopicDnDResponse>>,
      array: listActivitiesArray,
      enumToStringValueMap,
      updatedDateIds: isFetchingUpdatedDate ? undefined : dataUpdatedDate,
    });

    if (searchActivityQuery) {
      currentActivities = filterActivities(currentActivities, searchActivityQuery);
    }

    return currentActivities;
  }, [
    dataUpdatedDate,
    filters,
    isFetchingUpdatedDate,
    listActivitiesArray,
    searchActivityQuery,
  ]);
  const filteredActivitiesCount = filteredActivities.length;
  const resolvedTotalActivitiesCount = useMemo(() => {
    if (
      typeof totalActivitiesCount === 'number' &&
      !Number.isNaN(totalActivitiesCount) &&
      totalActivitiesCount >= filteredActivitiesCount
    ) {
      return totalActivitiesCount;
    }

    return filteredActivitiesCount;
  }, [filteredActivitiesCount, totalActivitiesCount]);

  const whatsappContactLinkFlag = useFeatureFlag('whatsapp-contact-link');
  const whatsappContactLink =
    whatsappContactLinkFlag.enabled && typeof whatsappContactLinkFlag.value === 'string'
      ? whatsappContactLinkFlag.value
      : process.env.REACT_APP_CONTACT_LINK || '';

  const addFilter = () => {
    blockFilterDropdown.current = false;

    const filterId = uuid();
    dispatch(ActivitiesActions.addActivityFilter(filterId));
    setLastFilterUpdateId(filterId);
  };

  const updateFilter = ({ op, values }: IUpdateFilter) => {
    const { filterId } = values;
    if (filterId) {
      dispatch(
        ActivitiesActions.updateActivityFilter({
          op,
          values,
        }),
      );
      setLastFilterUpdateId(filterId);
    }
  };

  const deleteFilter = (filterId: string) => {
    dispatch(ActivitiesActions.deleteActivityFilter(filterId));
  };

  const handleActiveListButton = (value: TypeListButtons) => {
    setActiveListButton(value);
    setSearchActivities('');
  };

  const handleMarkAsRead = useCallback(async (activityId: string) => {
    if (
      readNotificationsData &&
      readNotificationsData.activityId === activityId
    ) {
      dispatch(ActivitiesActions.readNotificationsData(null));

      const arrayReadDataNotifications = Object.values(readNotificationsData.readData);
      if (arrayReadDataNotifications.length > 0) {
        arrayReadDataNotifications.forEach(async (notification) => {
          await readMultipleNotificationsAsync(notification);
        });
      }
    }
  }, [readNotificationsData, dispatch, readMultipleNotificationsAsync]);

  const handleSetMoreActionsType = (value: TypeMoreActionsType) => {
    setMoreActionsType(value);
  };

  const handleObraPermission = (
    currentConstructionSiteId: number,
    currentUserId: string,
    currentObrasList?: IObraResponse[],
  ) => {
    if (currentObrasList && currentObrasList.length > 0) {
      const obraPermission = currentObrasList.find(
        (obra) => obra.ConstructionSiteId === currentConstructionSiteId,
      );
      if (obraPermission) {
        const haveObraPermission = obraPermission.SharedUsers.find(
          (user: any) => user.UserFk === currentUserId,
        );
        if (haveObraPermission) {
          return;
        }
      }
    }

    toastHandler.showError(i18n.t('messages:toasts.obraNoPermission'));
    return navigate('/', { replace: true });
  };

  const handleUserPermissionExternalLink = (
    index: string,
    currentListActivities: IDragAndDropDataFormat,
  ) => {
    const activity = currentListActivities.activities[index];

    const haveVisualizationPermission = AccessPermission.visualizationActivitiePermission(
      userInfo,
      activity.CreationAuthor,
      activity.TopicAssignedUsers,
      activity.Visibility,
    );

    if (haveVisualizationPermission) {
      navigate('?');
      setMoreActionsType('update');
      return setCurrentShowActivity(index);
    }

    toastHandler.showError(i18n.t('messages:toasts.activityNoPermission'));
    handleCloseShowActivity();
    return navigate('?', { replace: true });
  };

  const handleUpdateListActivities = () => {
    toastHandler.externalDeleteUpdateToast();
    const request: ITopicRequest = {
      csId: constructionSiteId,
      stagesFeatureValidate: stagesFeatureActivityValidate,
      filtersId: filtersIdQuery,
    };
    dispatch(ActivitiesActions.listActivities(request));
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY_ACTIVITIES_UPDATED_DATE] });
    refetchInAppTopic();
    setLastModifiedLoading(true);
  };

  const handleOpenCreateActivity = () => {
    if (workActivityQuery) {
      navigate(`?`);
    }
    setMoreActionsType(null);
    setCurrentShowActivity(null);
    setShowCreateActivity(true);
  };

  const handleOpenShowActivity = (
    activityId: string,
    moreActionsType?: TypeMoreActionsType,
  ) => {
    if (workActivityQuery) {
      navigate(`?`);
    }
    setMoreActionsType(moreActionsType || 'update');
    setCurrentShowActivity(activityId);
    setShowCreateActivity(true);
  };

  const handleDuplicateActivity = (activityId: string) => {
    if (workActivityQuery) {
      navigate(`?`);
    }
    setMoreActionsType('duplicate');
    setCurrentShowActivity(activityId);
    setShowCreateActivity(true);
  };

  const getUpdatedActivity = useCallback(() => {
    if (listActivities?.activities && currentShowActivity) {
      if (moreActionsType) {
        if (['update', 'createAndOpen'].includes(moreActionsType)) {
          return listActivities.activities[currentShowActivity];
        }
        if (['createAndCopy', 'duplicate'].includes(moreActionsType)) {
          return {
            ...listActivities.activities[currentShowActivity],
            Guid: null,
          } as ITopicDnDResponseOptionalGuid;
        }
      }
    }
    return null;
  }, [listActivities?.activities, currentShowActivity, moreActionsType]);

  const handleCloseOpenCreateActivity = () => {
    handleCloseShowActivity();
    clear('useActivity-handleCloseOpenCreateActivity');
    set('useActivity-handleCloseOpenCreateActivity', () => {
      handleOpenCreateActivity();
    }, 0);
  };

  const handleOpenActivityViewpoint = useCallback((topicId: string, viewpointId: string) => {
    // Redirect to the viewer keeping the same construction site so it can restore the saved viewpoint.
    navigate(`/obras/viewer/detail/${constructionSiteId}?topicId=${topicId}&viewpointId=${viewpointId}`);
  }, [navigate, constructionSiteId]);

  const handleCloseShowActivity = () => {
    setMoreActionsType(null);
    setCurrentShowActivity(null);
    setShowCreateActivity(false);
  };

  const handleShowFilters = (show: boolean) => {
    if (show) {
      blockFilterDropdown.current = true;
    }
    setShowFilters(show);
  };

  const handleShowFiltersShare = (show: boolean) => {
    if (!shareLink) {
      const filtersFormat: IFiltersData[] = [];

      for (const filter of Object.values(filters)) {
        const { topicId, operation, value, description, properties } = filter;

        let currentValue = [];
        const extraData: Record<string, any> = {};

        if (value) {
          currentValue = Array.isArray(filter.value) ? filter.value : [filter.value];
        }
        if (operation) {
          extraData['operation'] = operation;
        }
        if (properties) {
          extraData['properties'] = properties;
        }
        if (description) {
          extraData['description'] = description;
        }

        filtersFormat.push({
          Field: topicKeysRequestFormat[topicId],
          Values: currentValue,
          ExtraData: extraData,
        });
      }

      const request: IFiltersRequest = {
        Type: FilterType.Activity,
        ConstructionSiteFk: constructionSiteId,
        Filters: filtersFormat,
        CreateShortURL: true,
      };
      dispatch(ActivitiesActions.shareLinkActivity(request));
    }
    setShowFiltersShare(show);
  };

  const clearShareLink = () => {
    dispatch(ActivitiesActions.shareLinkActivityClear());
  };

  const clearAllFilters = () => {
    dispatch(ActivitiesActions.clearAllActivityFilters());
    handleShowFilters(false);
  };

  const handlePrintList = () => {
    window.print();
  };

  const handlePrintDetails = () => {
    setShowPrintDetailsModal(true);
  };

  const handleClosePrintDetailsModal = () => {
    setShowPrintDetailsModal(false);
  };

  const handleConfirmPrintDetailsModal = async (settings: IPrintDetailsSettings) => {
    const hasSelectedData =
      settings.includeBasicInfo ||
      settings.includeAttachments ||
      settings.includeComments ||
      settings.includeHistory;

    if (!hasSelectedData) {
      toastHandler.showError('Selecione ao menos um dado para impressão.');
      return;
    }

    const printQuery = new URLSearchParams({
      scope: settings.activitiesScope,
      orientation: settings.orientation,
      showBasicInfo: String(settings.includeBasicInfo),
      showAttachments: String(settings.includeAttachments),
      showComments: String(settings.includeComments),
      showHistory: String(settings.includeHistory),
    });

    if (settings.activitiesScope === 'filtered') {
      if (filtersIdQuery) {
        printQuery.set('filterId', filtersIdQuery);
      }

      if (searchActivityQuery) {
        printQuery.set('searchActivity', searchActivityQuery);
      }

      if (!filtersIdQuery && hasFilters) {
        try {
          const filtersFormat: IFiltersData[] = [];

          for (const filter of Object.values(filters)) {
            const { topicId, operation, value, description, properties } = filter;

            let currentValue: any[] = [];
            const extraData: Record<string, any> = {};

            if (value) {
              currentValue = Array.isArray(value) ? value : [value];
            }
            if (operation) {
              extraData['operation'] = operation;
            }
            if (properties) {
              extraData['properties'] = properties;
            }
            if (description) {
              extraData['description'] = description;
            }

            filtersFormat.push({
              Field: topicKeysRequestFormat[topicId],
              Values: currentValue,
              ExtraData: extraData,
            });
          }

          const request: IFiltersRequest = {
            Type: FilterType.Activity,
            ConstructionSiteFk: constructionSiteId,
            Filters: filtersFormat,
            CreateShortURL: true,
          };

          const { data: { data } } = await FilterApi.shareLinkActivity(request);

          if (data?.FilterId) {
            printQuery.set('filterId', data.FilterId);
          }
        } catch {
          toastHandler.showError('Erro ao salvar filtros para exportação.');
          return;
        }
      }
    }

    const printUrl = `/obras/activities/export/${constructionSiteId}?${printQuery.toString()}`;
    const printWindow = window.open(printUrl, '_blank', 'noopener,noreferrer');

    if (!printWindow) {
      toastHandler.showError('Não foi possível abrir a página de impressão. Verifique o bloqueador de pop-up.');
      return;
    }

    setShowPrintDetailsModal(false);
  };

  const handleNotImplementedAction = (actionLabel: string) => {
    toastHandler.showInfo(`${actionLabel} em desenvolvimento.`);
  };

  const handleExportCsv = () => {
    handleNotImplementedAction('Exportação .CSV');
  };

  const handleExportXml = () => {
    handleNotImplementedAction('Exportação .XML');
  };

  const handleExportBcf = () => {
    handleNotImplementedAction('Exportação .BCF');
  };

  const handleImportBcf = () => {
    handleNotImplementedAction('Importação .BCF');
  };

  const toggleAiModal = () => {
    setShowAiModal((showAiModal) => !showAiModal);
  };

  const upgradePlanRedirect = (role: number, planOrder: number) => {
    Mixpanel.track({
      name: 'CALL_T0_ACTION',
      props: {
        origin: 'activities',
        originPath: window.location.pathname,
      },
      userInfo,
      currentListTenant: currentTenant,
      tenantIds:
        tenants && tenants.length > 0
          ? (tenants
            ?.map((tenant) => tenant?.TenantId)
            .filter((tenantId) => tenantId !== undefined) as number[])
          : undefined,
    });

    if (role === 1) {
      if (planOrder === 4) {
        window.open(whatsappContactLink);
      } else {
        navigate('/faturamento/planos');
      }
    } else {
      window.open('https://bim.maletadoengenheiro.com.br/seja-premium');
    }
  };

  const getActionsToolbar = (): ITableActionTopbarButton[] => {
    return [
      {
        icon: 'share',
        iconCustomSize: 18,
        description: t('toolbar.share'),
        hidden: false,
        action: () => handleShowFiltersShare(!showFiltersShare),
      },
      {
        icon: 'filtrar',
        iconCustomSize: 14,
        description: t('toolbar.filters'),
        hidden: false,
        action: () => handleShowFilters(!showFilters),
      },
      {
        icon: 'atualizar',
        iconCustomSize: 14,
        description: t('toolbar.refresh'),
        hidden: false,
        action: () => handleUpdateListActivities(),
      },
      {
        icon: 'adicionarAlt',
        iconCustomSize: 18,
        description: t('toolbar.newActivity'),
        hidden: !AccessPermission.createOrEditActivitiePermission(),
        action: () => handleOpenCreateActivity(),
      },
      {
        icon: 'moreActions',
        hidden: false,
        dropdownMenu: (toggleDropdown: () => void) => (
          <MoreFiltersActions
            onPrintList={handlePrintList}
            onPrintDetails={handlePrintDetails}
            onExportCsv={handleExportCsv}
            onExportXml={handleExportXml}
            onExportBcf={handleExportBcf}
            onImportBcf={handleImportBcf}
            onResetFilters={() => clearAllFilters()}
            onCancel={() => toggleDropdown()}
          />
        ),
      },
    ];
  };

  useEffect(() => {
    const request: ITopicRequest = {
      csId: constructionSiteId,
      stagesFeatureValidate: stagesFeatureActivityValidate,
      filtersId: filtersIdQuery,
    };
    dispatch(ActivitiesActions.listActivities(request));
    refetchInAppTopic();

    return () => {
      clear('useActivity-firstOpenFilters');
      clear('useActivity-handleCloseOpenCreateActivity');
      dispatch(ActivitiesActions.clearAllActivityFilters());
    };
  }, []);

  useEffect(() => {
    if (constructionSiteId && userInfo?.Id && !externalGlobalLoading) {
      const constructionSiteIdNumber = Number(constructionSiteId);
      handleObraPermission(constructionSiteIdNumber, userInfo.Id, obrasList);
    }

    if (workActivityQuery) {
      if (!isNotifQuery || (isNotifQuery && firstRenderNotif.current === 0)) {
        setShowCreateActivity(true);
      }

      if (isNotifQuery && firstRenderNotif.current === 0) {
        const request: ITopicRequest = {
          csId: constructionSiteId,
          stagesFeatureValidate: stagesFeatureActivityValidate,
          filtersId: filtersIdQuery,
        };
        dispatch(ActivitiesActions.listActivities(request));
        refetchInAppTopic();
        firstRenderNotif.current = 1;
      }

      if (
        !!listActivities && listActivities.activities &&
        (!isNotifQuery || (isNotifQuery && firstRenderNotif.current === 1))
      ) {
        handleMarkAsRead(workActivityQuery);

        if (
          userInfo && userInfo.Id &&
          listActivities.activities.hasOwnProperty(workActivityQuery)
        ) {
          firstRenderNotif.current = 0;
          handleUserPermissionExternalLink(
            workActivityQuery,
            listActivities,
          );
        }

        if (!listActivities.activities.hasOwnProperty(workActivityQuery)) {
          toastHandler.showError(i18n.t('messages:toasts.activityNotExists'));
          handleCloseShowActivity();
          firstRenderNotif.current = 0;
          navigate('?', { replace: true });
        }
      }
    }
  }, [
    listActivities,
    obrasList,
    userInfo,
    constructionSiteId,
    externalGlobalLoading,
    workActivityQuery,
    isNotifQuery,
  ]);

  useEffect(() => {
    if (lastModifiedLoading && listActivities) {
      setLastModifiedLoading(false);
    }
  }, [listActivities]);

  useEffect(() => {
    if (searchActivityQuery && firstRender.current === 0) {
      setSearchActivities(searchActivityQuery || '');
      setSearchActivitiesDefault(searchActivityQuery || undefined);
    }

    if (searchActivities) {
      const searchQuery = `?searchActivity=${encodeURIComponent(searchActivities)}`;

      navigate(searchQuery);
      firstRender.current = 1;
    } else {
      if (firstRender.current < 1) { return; }
      navigate(`?`);
    }
  }, [searchActivities]);

  useEffect(() => {
    if (
      !filtersIdQuery &&
      firstRenderFilters.current &&
      !hasFilters &&
      (AppStorage.GetItem(getFiltersKey(constructionSiteId), 'session') ||
        fromUpDateQuery || toUpDateQuery)
    ) {
      let useFilter: FiltersRecordType = {};

      if (AppStorage.GetItem(getFiltersKey(constructionSiteId), 'session')) {
        useFilter = JSON.parse(
          AppStorage.GetItem(getFiltersKey(constructionSiteId), 'session') ?? '',
        );
      }

      let hasChanged = false;
      if (fromUpDateQuery || toUpDateQuery) {
        const hasTwoValues = fromUpDateQuery && toUpDateQuery;
        const valueUpdatedDate = hasTwoValues
          ? [fromUpDateQuery, toUpDateQuery]
          : (fromUpDateQuery || toUpDateQuery);

        for (const [key, filter] of Object.entries(useFilter)) {
          if (filter.topicId === 'UpdatedDate') {
            useFilter[key].operation = hasTwoValues ? 'between' : 'equal';
            useFilter[key].value = valueUpdatedDate;
            useFilter[key].description = valueUpdatedDate as string;
            hasChanged = true;
          }
        }

        if (!hasChanged) {
          const filterId = uuid();
          useFilter[filterId] = {
            topicId: 'UpdatedDate',
            operation: hasTwoValues ? 'between' : 'equal',
            value: valueUpdatedDate,
            description: valueUpdatedDate as string,
          };
          hasChanged = true;
        }
      }

      dispatch(ActivitiesActions.populatedActivityFilter(useFilter));
      if (JSON.stringify(useFilter) !== '{}') {
        handleShowFilters(true);
      }

      clear('useActivity-firstOpenFilters');
      set('useActivity-firstOpenFilters', () => {
        firstOpenFilters.current = false;
        if (hasChanged) {
          navigate('?');
        }
      }, 1000);
      firstRenderFilters.current = false;
    }

    if (filtersIdQuery && firstRenderFilters.current && hasFilters) {
      handleShowFilters(true);

      clear('useActivity-firstOpenFilters');
      set('useActivity-firstOpenFilters', () => {
        firstOpenFilters.current = false;
      }, 1000);
      firstRenderFilters.current = false;
    }

    const filtersString = JSON.stringify(filters);

    AppStorage.SetItem(getFiltersKey(constructionSiteId), filtersString, 'session');
    clearShareLink();
  }, [filters, hasFilters, constructionSiteId]);

  useEffect(() => {
    if (userInfo && userInfo.Id) {
      if (
        topicSignalRHasUpdate &&
        topicSignalRHasUpdate.csId === constructionSiteId &&
        topicSignalRHasUpdate.UpdaterUserId !== userInfo.Id &&
        !firstRenderSignal.current
      ) {
        switch (topicSignalRHasUpdate.Visibility) {
          case TopicVisibilityEnum.Public:
            toastHandler.showActivitieHasUpdate(
              'Atualização disponível. Clique para atualizar.',
              '',
              handleUpdateListActivities,
            );
            break;

          case TopicVisibilityEnum.Private:
            if (
              userInfo.Id &&
              topicSignalRHasUpdate.ResponsibleUsersId.includes(userInfo.Id)
            ) {
              toastHandler.showActivitieHasUpdate(
                'Atualização disponível. Clique para atualizar.',
                '',
                handleUpdateListActivities,
              );
            }
            break;

          default:
            return;
        }
        dispatch(ActivitiesActions.topicSignalRHasUpdate(null));
      }
      firstRenderSignal.current = false;
    }
  }, [topicSignalRHasUpdate, userInfo]);

  useEffect(() => {
    if (!isLoadingCreate && !isLoadingUpdate) {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_ACTIVITIES_UPDATED_DATE] });
      if (
        !!constructionSiteId &&
        (dayjs(filterUpdatedDate.from).isValid() ||
          dayjs(filterUpdatedDate.to).isValid())
      ) {
        refetchUpdatedDate();
      }
    }
  }, [
    isLoadingCreate,
    isLoadingUpdate,
    constructionSiteId,
    filterUpdatedDate.from,
    filterUpdatedDate.to,
    refetchUpdatedDate,
  ]);

  return {
    activeListButton,
    searchActivitiesDefault,
    height,
    width,
    query,
    showCreateActivity,
    currentShowActivity,
    lastModifiedLoading,
    showFilters,
    showFiltersShare,
    filters,
    hasFilters,
    firstRenderFilters: firstOpenFilters.current,
    lastFilterUpdateId,
    blockFilterDropdown: blockFilterDropdown.current,
    inAppTopicNotifications,
    showAiModal,
    showPrintDetailsModal,
    filteredActivitiesCount,
    totalActivitiesCount: resolvedTotalActivitiesCount,
    moreActionsType,
    handleSetMoreActionsType,
    toggleAiModal,
    handleClosePrintDetailsModal,
    handleConfirmPrintDetailsModal,
    handleActiveListButton,
    upgradePlanRedirect,
    getActionsToolbar,
    setSearchActivities,
    setSearchLoading,
    handleOpenCreateActivity,
    handleOpenShowActivity,
    handleDuplicateActivity,
    handleCloseShowActivity,
    handleCloseOpenCreateActivity,
    handleOpenActivityViewpoint,
    handleShowFilters,
    handleShowFiltersShare,
    addFilter,
    updateFilter,
    deleteFilter,
    getUpdatedActivity,
  };
};

export default useActivity;
